import { useEffect, useState, useCallback, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { useDispatch, useSelector } from "react-redux";
import { logout as logoutAction, loginStart, loginFailure } from "@/store/slices/authSlice";
import { loginRequest } from "@/config/azureEntraIdConfig";
import { auditLogService } from "@/services/auditLogService";
import { RootState, persistor, store } from "@/store/store";
import {
  clearSilentSsoFlags,
  LOGIN_INITIATED_KEY,
  SKIP_SILENT_SSO_KEY,
} from "@/utils/silentSso";
import { establishSession, getCurrentMsalAccount } from "@/utils/sessionIdentity";

// Flag to know if this tab initiated the logout flow
let isLocalLogoutInProgress = false;

// Simple cross-tab logout synchronization using BroadcastChannel
class LogoutBroadcast {
  private static channel = new BroadcastChannel('oneview-logout-sync');
  private static listeners: Set<() => void> = new Set();

  static broadcastLogout() {
    this.channel.postMessage({ type: 'LOGOUT', timestamp: Date.now() });
  }

  static listenForLogout(callback: () => void) {
    this.listeners.add(callback);

    // Set up the message handler only once
    if (this.listeners.size === 1) {
      this.channel.onmessage = (event) => {
        if (event.data.type === 'LOGOUT') {
          // Call all registered listeners
          this.listeners.forEach(listener => {
            try {
              listener();
            } catch (error) {
              console.error('Error in logout listener:', error);
            }
          });
        }
      };
    }

    // Return cleanup function
    return () => {
      this.listeners.delete(callback);
    };
  }
}

/**
 * Custom hook for Azure Entra ID authentication
 * Provides authentication state and methods
 */
export const useAzureAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  // Latest persisted user, read by effects that must not re-run when it changes
  const userRef = useRef(user);
  userRef.current = user;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get the current active account
   */
  const getActiveAccount = useCallback((): AccountInfo | null => {
    if (accounts.length > 0) {
      return getCurrentMsalAccount(instance);
    }
    return null;
  }, [accounts, instance]);

  /**
   * Login using redirect
   * @param redirectStartPage - page to return to after login (defaults to the current page)
   */
  const loginRedirect = useCallback(async (redirectStartPage?: string) => {
    // Prevent multiple simultaneous login attempts
    if (inProgress !== 'none') {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    dispatch(loginStart());

    try {
      // Mark that the user explicitly initiated a login flow
      sessionStorage.setItem(LOGIN_INITIATED_KEY, "true");
      clearSilentSsoFlags();

      await instance.loginRedirect({
        ...loginRequest,
        prompt: "select_account",
        extraQueryParameters: {
          max_age: "0"
        },
        ...(redirectStartPage && { redirectStartPage }),
      });
      // Note: Code after this line will NOT execute because browser redirects
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      setError(errorMessage);
      setIsLoading(false);
      console.error("Login redirect error:", err);
    }
  }, [instance, dispatch, inProgress]);

  /**
   * Login using popup
   */
  const loginPopup = useCallback(async (): Promise<AuthenticationResult | null> => {
    setIsLoading(true);
    setError(null);
    dispatch(loginStart());

    try {
      // Mark that the user explicitly initiated a login flow
      sessionStorage.setItem(LOGIN_INITIATED_KEY, "true");
      clearSilentSsoFlags();

      const response = await instance.loginPopup({
        ...loginRequest,
        prompt: "select_account",
        extraQueryParameters: {
          max_age: "0"
        }
      });

      if (response && response.account) {
        establishSession(instance, dispatch, response.account, userRef.current);
      }

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      setError(errorMessage);
      dispatch(loginFailure(errorMessage));
      
      console.error("Login popup error:", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [instance, dispatch]);

  /**
   * Logout (app-only)
   *
   * Behaviour:
   * - Clear MSAL cache in this tab (by clearing storage)
   * - Clear sessionStorage and localStorage (including persisted Redux state)
   * - Propagate logout to all open tabs
   * - DOES NOT call Azure logout, so no AAD UI is shown
   */
  const logout = useCallback(async (reason: 'USER_INITIATED' | 'SESSION_TIMEOUT' | 'FORCED' = 'USER_INITIATED') => {
    setIsLoading(true);
    setError(null);

    try {
      const account = getActiveAccount();
      if (account) {
        // Mark that this tab is the one initiating logout
        isLocalLogoutInProgress = true;

        try {
          await auditLogService.logAuditEvent(
            'USER_LOGOUT',
            auditLogService.getSessionId(),
            { reason }
          );
        } catch (error) {
          // Don't break logout flow if audit log fails
          console.error('Failed to log logout event:', error);
        }

        // Clear ALL data immediately in this tab to prevent any re-authentication.
        // This also clears MSAL cache when using storage-backed cache (default).
        sessionStorage.clear();
        localStorage.clear();

        // Logout is app-only (the Entra session stays), so stop /login from
        // silently signing the user straight back in
        sessionStorage.setItem(SKIP_SILENT_SSO_KEY, "true");

        // Reset Redux state
        store.dispatch({
          type: 'auth/logout',
          payload: undefined
        });
        dispatch(logoutAction());

        // Purge persistor
        await persistor.purge().catch((error) => {
          console.warn('Failed to purge persistor:', error);
        });

        // Broadcast logout to other tabs so they clear state as well
        LogoutBroadcast.broadcastLogout();

        // App-only navigation to login (no Azure logout UI)
        window.location.href = "/login";
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Logout failed";
      setError(errorMessage);
      console.error("Logout error:", err);
      // If something failed, allow this tab to react to future broadcasts again
      isLocalLogoutInProgress = false;
    } finally {
      setIsLoading(false);
    }
  }, [instance, getActiveAccount, dispatch]);

  /**
   * Get access token silently
   */
  const getAccessToken = useCallback(
    async (scopes: string[] = loginRequest.scopes): Promise<string | null> => {
      const account = getActiveAccount();
      if (!account) {
        return null;
      }

      try {
        const response = await instance.acquireTokenSilent({
          scopes,
          account,
        });
        return response.accessToken;
      } catch (err) {
        console.error("Token acquisition error:", err);
        // If silent token acquisition fails, you might want to trigger an interactive login
        return null;
      }
    },
    [instance, getActiveAccount]
  );

  /**
   * Track if we've logged the login audit event for this session
   */
  const hasLoggedLoginAudit = useRef(false);

  /**
   * Clear any stale auth state on mount if not actually authenticated
   */
  useEffect(() => {
    if (!isAuthenticated && !user) {
      // Clear any stale auth data from localStorage
      localStorage.clear();
    }
  }, [isAuthenticated, user]);

  /**
   * Sync authentication state with Redux on mount and when accounts change
   * Also logs USER_LOGIN audit event once per login session
   */
  useEffect(() => { // NOSONAR
    // Only sync MSAL auth into Redux if login was explicitly initiated
    const loginInitiated = sessionStorage.getItem(LOGIN_INITIATED_KEY) === "true";

    const account = accounts.length > 0 ? getCurrentMsalAccount(instance) : null;

    if (
      isAuthenticated &&
      account &&
      loginInitiated &&
      // Builds the session from the current Microsoft identity (wipes a different user's state)
      establishSession(instance, dispatch, account, userRef.current)
    ) {
      if (!hasLoggedLoginAudit.current) {
        hasLoggedLoginAudit.current = true;
        
        const loginLoggedKey = 'audit_login_logged';
        const hasLoggedThisSession = sessionStorage.getItem(loginLoggedKey);
        
        if (!hasLoggedThisSession) {
          sessionStorage.setItem(loginLoggedKey, 'true');
          
          const logLoginAudit = async () => {
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              
              await auditLogService.logAuditEvent(
                'USER_LOGIN',
                auditLogService.getSessionId()
              );
            } catch (error) {
              console.error('[Audit Log] Failed to log login event:', error);
            }
          };
          
          logLoginAudit();
        }
      }
    }

    if (!isAuthenticated && hasLoggedLoginAudit.current) {
      hasLoggedLoginAudit.current = false;
      sessionStorage.removeItem('audit_login_logged');
    }
  }, [isAuthenticated, accounts, dispatch, instance]);

  // New tabs no longer request tokens as the persisted user: that could keep a previous
  // Microsoft account's session alive. ProtectedRoute sends unverified tabs through /login,
  // which re-establishes the session from the current Microsoft identity.

  /**
   * Listen for logout broadcasts from other tabs
   */
  useEffect(() => {
    const cleanup = LogoutBroadcast.listenForLogout(async () => {
      // If this tab initiated the logout, we already handled cleanup above
      if (isLocalLogoutInProgress) {
        return;
      }

      console.log('Received logout broadcast from another tab');

      // Clear all data immediately
      // Clear sessionStorage completely (removes MSAL tokens)
      sessionStorage.clear();
      // Clear localStorage completely (removes Redux persisted state)
      localStorage.clear();
      // Stay logged out on /login instead of silently signing back in
      sessionStorage.setItem(SKIP_SILENT_SSO_KEY, "true");

      // Reset Redux state
      store.dispatch({
        type: 'auth/logout',
        payload: undefined
      });
      dispatch(logoutAction());

      // Purge persistor
      await persistor.purge().catch((error) => {
        console.warn('Failed to purge persistor:', error);
      });

      // Navigate to login page immediately
      // sessionStorage is already cleared above, so MSAL tokens are gone
      window.location.href = '/login';
    });

    return cleanup;
  }, [dispatch]);

  return {
    isAuthenticated,
    account: getActiveAccount(),
    accounts,
    inProgress,
    isLoading,
    error,
    loginRedirect,
    loginPopup,
    logout,
    getAccessToken,
  };
};

